/* eslint-disable no-nested-ternary */
import { useCallback, useMemo, useState } from 'react';
import { omitBy, isNil } from 'lodash';
import moment from 'moment-timezone';
import {
  Button,
  Select,
  Tab,
  Table,
  TableController,
  Tabs,
  theme,
  TooltipBlock,
  ANNOUNCEMENT_STATUS,
  ANNOUNCEMENT_TYPE,
} from 'bs-unified-ui';
import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { fromQueryString, toQueryString } from '~/utils/queryString';
import DayPicker from '~/components/common/DayPicker';
import {
  ANNOUNCEMENT_LIST_LIMIT,
  ANNOUNCEMENT_STATUS_COLOR,
  ANNOUNCEMENT_TAB,
  ANNOUNCEMENT_TAB_LIST,
  ANNOUNCEMENT_TYPE_OPTIONS,
  DISPLAY_FORMAT,
  POS_TYPE,
  STATUS_OPTIONS,
} from '~/constants';
import { useGetListAnnouncement } from '~/services/api/announcement';
import DataLoading from '~/components/common/DataLoading';
import { Icon } from '~/components/common/Icon';
import { useGetListUser } from '~/services/api/auth';
import Layout from '../components/Layout';
import ModeSetting from './ModeSetting';
import Preview from '../CreateOrEdit/sections/Preview';

const { SearchForm, Controller, Sorting } = TableController;

const List = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [previewAnnouncement, setPreviewAnnouncement] = useState(null);

  const filtersFromLocation = useMemo(
    () =>
      fromQueryString(location.search, {
        arrayFormat: 'none',
      }),
    [location.search],
  );

  const [sort, setSort] = useState({
    sortId: filtersFromLocation.sort_by,
    direction:
      // eslint-disable-next-line no-nested-ternary
      Number(filtersFromLocation.sort) > 0
        ? 'asc'
        : Number(filtersFromLocation.sort) < 0
        ? 'desc'
        : '',
  });

  const {
    type,
    tab = ANNOUNCEMENT_TAB.ACTIVE,
    status,
    search,
    created_by,
    start_date,
    end_date,
    page = 1,
  } = filtersFromLocation;

  const apiParams = useMemo(() => {
    const params = { ...filtersFromLocation };
    if (tab === ANNOUNCEMENT_TAB.ACTIVE) {
      params.limit = -1;
    } else {
      params.limit = ANNOUNCEMENT_LIST_LIMIT;
    }
    return params;
  }, [filtersFromLocation, tab]);

  const { data = {}, isFetching } = useGetListAnnouncement(apiParams);

  const { data: listUser } = useGetListUser({});

  const listAnnouncement = useMemo(() => data?.items || [], [data]);

  const applyFilter = useCallback(
    (newParams = {}, shouldResetFilters = false) => {
      const newLocationSearch = shouldResetFilters
        ? newParams
        : { ...filtersFromLocation, ...newParams };

      const filteredParams = omitBy(
        newLocationSearch,
        (value) => value === '' || isNil(value),
      );

      navigate({
        pathname: location.pathname,
        search: toQueryString(filteredParams),
      });
    },
    [filtersFromLocation, navigate, location.pathname],
  );

  const applyFilterAndResetPage = useCallback(
    (newParams = {}) => {
      applyFilter({ ...newParams, page: 1 });
    },
    [filtersFromLocation, applyFilter],
  );

  const handleChangeTab = useCallback(
    (t) => {
      applyFilter({ tab: t }, true);
    },
    [applyFilter],
  );

  const handleChangeType = (value) => {
    applyFilterAndResetPage({ type: value });
  };

  const handleChangeStatus = (value) => {
    applyFilterAndResetPage({ status: value });
  };

  const handleChangeCreatedBy = (value) => {
    applyFilterAndResetPage({ created_by: value });
  };

  const handleChangeAppliedTo = (value) => {
    applyFilterAndResetPage({ apply_to_pos: value });
  };

  const handleChangeStartDate = (value) => {
    // Convert from DISPLAY_FORMAT (MMM D, YYYY) to YYYY-MM-DD for API
    const formattedDate = value
      ? moment(value, DISPLAY_FORMAT).format('YYYY-MM-DD')
      : '';
    applyFilterAndResetPage({ start_date: formattedDate });
  };

  const handleChangeEndDate = (value) => {
    // Convert from DISPLAY_FORMAT (MMM D, YYYY) to YYYY-MM-DD for API
    const formattedDate = value
      ? moment(value, DISPLAY_FORMAT).format('YYYY-MM-DD')
      : '';
    applyFilterAndResetPage({ end_date: formattedDate });
  };

  const createdByOptions = useMemo(() => {
    if (!listUser || !Array.isArray(listUser)) return [];

    return listUser.map((user) => ({
      // eslint-disable-next-line no-underscore-dangle
      value: user._id,
      label: user.full_name,
    }));
  }, [listUser]);

  const handleClickDetail = (id) => {
    navigate(`/announcement/edit/${id}`);
  };

  const appliedToOptions = [
    { value: POS_TYPE.ALL, label: 'All clients' },
    { value: 'specific', label: 'Specific clients only' },
  ];

  const sortOptionActive = [
    { value: 'status', label: 'Status' },
    { value: 'schedule', label: 'Schedule' },
    { value: 'type', label: 'Type of announcement' },
  ];

  const sortOptionArchived = [
    { value: 'end_time', label: 'Ended time' },
    { value: 'start_time', label: 'Started time' },
    { value: 'type', label: 'Type of announcement' },
  ];

  const renderFilter = () => {
    if (tab === ANNOUNCEMENT_TAB.ACTIVE) {
      return (
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Select
            clearable
            options={ANNOUNCEMENT_TYPE_OPTIONS}
            placeholder='Type of announcement'
            value={type}
            onChange={(e) => handleChangeType(e.target.value)}
            clearCallback={() => handleChangeType('')}
          />
          <Select
            clearable
            options={STATUS_OPTIONS}
            placeholder='Status'
            value={status}
            onChange={(e) => handleChangeStatus(e.target.value)}
            clearCallback={() => handleChangeStatus('')}
          />
          <Select
            clearable
            options={createdByOptions}
            placeholder='Created by'
            value={created_by}
            onChange={(e) => handleChangeCreatedBy(e.target.value)}
            clearCallback={() => handleChangeCreatedBy('')}
          />
          <Sorting
            options={sortOptionActive}
            value={sort}
            onChange={(val) => {
              const { sortId, ...newValues } = {
                ...filtersFromLocation,
                sort_by: val.sortId,
                sort:
                  val.direction === 'asc'
                    ? 1
                    : val.direction === 'desc'
                    ? -1
                    : '',
              };
              navigate({
                pathname: location.pathname,
                search: toQueryString(newValues),
              });
              setSort(val);
            }}
          />
        </Box>
      );
    }

    if (tab === ANNOUNCEMENT_TAB.ARCHIVED) {
      return (
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <Select
            clearable
            options={ANNOUNCEMENT_TYPE_OPTIONS}
            placeholder='Type of announcement'
            value={type}
            onChange={(e) => handleChangeType(e.target.value)}
            clearCallback={() => handleChangeType('')}
          />
          <Select
            clearable
            options={appliedToOptions}
            placeholder='Applied to'
            value={filtersFromLocation.apply_to_pos}
            onChange={(e) => handleChangeAppliedTo(e.target.value)}
            clearCallback={() => handleChangeAppliedTo('')}
          />
          <Box
            sx={{
              'width': 140,
              '& input::placeholder': {
                color: theme.foreground.black,
                opacity: 1,
              },
            }}
          >
            <DayPicker
              value={
                start_date
                  ? moment(start_date, 'YYYY-MM-DD').format(DISPLAY_FORMAT)
                  : ''
              }
              onChange={(date) => {
                const formattedDate = date
                  ? moment(date).format(DISPLAY_FORMAT)
                  : '';
                handleChangeStartDate(formattedDate);
              }}
              onClear={() => {
                handleChangeStartDate('');
              }}
              placeholder='Started time'
              dateFormat={DISPLAY_FORMAT}
            />
          </Box>
          <Box
            sx={{
              'width': 140,
              '& input::placeholder': {
                color: theme.foreground.black,
                opacity: 1,
              },
            }}
          >
            <DayPicker
              value={
                end_date
                  ? moment(end_date, 'YYYY-MM-DD').format(DISPLAY_FORMAT)
                  : ''
              }
              onChange={(date) => {
                const formattedDate = date
                  ? moment(date).format(DISPLAY_FORMAT)
                  : '';
                handleChangeEndDate(formattedDate);
              }}
              onClear={() => {
                handleChangeEndDate('');
              }}
              placeholder='Ended time'
              dateFormat={DISPLAY_FORMAT}
            />
          </Box>
          <Select
            clearable
            options={createdByOptions}
            placeholder='Created by'
            value={created_by}
            onChange={(e) => handleChangeCreatedBy(e.target.value)}
            clearCallback={() => handleChangeCreatedBy('')}
          />
          <Sorting
            options={sortOptionArchived}
            value={sort}
            onChange={(val) => {
              const { sortId, ...newValues } = {
                ...filtersFromLocation,
                sort_by: val.sortId,
                sort:
                  val.direction === 'asc'
                    ? 1
                    : val.direction === 'desc'
                    ? -1
                    : '',
              };
              navigate({
                pathname: location.pathname,
                search: toQueryString(newValues),
              });
              setSort(val);
            }}
          />
        </Box>
      );
    }

    return null;
  };

  const archivedColumns = useMemo(
    () => [
      {
        id: 'name',
        title: 'Name',
        renderComponent: (item) => (
          <TooltipBlock title={item.name}>
            <Typography
              variant='fs14'
              color={theme.foreground.black}
              fontWeight={600}
            >
              {item.name}
            </Typography>
          </TooltipBlock>
        ),
        sx: {
          minWidth: 100,
          maxWidth: 200,
        },
      },
      {
        id: 'type',
        title: 'Type of announcement',
        renderComponent: (item) => (
          <Typography variant='fs14' color={theme.foreground.black}>
            {
              ANNOUNCEMENT_TYPE_OPTIONS.find((op) => op.value === item.type)
                ?.label
            }
          </Typography>
        ),
        sx: {
          minWidth: 100,
        },
      },
      {
        id: 'apply_to_pos',
        title: 'Applied to',
        renderComponent: (item) => (
          <Typography color={theme.foreground.black} fontSize={14}>
            {item.apply_to_pos === POS_TYPE.ALL
              ? `All clients`
              : 'Specific clients only'}
          </Typography>
        ),
        sx: {
          minWidth: 160,
        },
      },
      {
        id: 'start_time',
        title: 'Started time',
        renderComponent: (item) => (
          <Typography color={theme.foreground.black} fontSize={14}>
            {moment(item.start_time).format('MMM D, YYYY')}
          </Typography>
        ),
      },
      {
        id: 'end_time',
        title: 'Ended time',
        renderComponent: (item) => (
          <Typography color={theme.foreground.black} fontSize={14}>
            {moment(item.end_time).format('MMM D, YYYY')}
          </Typography>
        ),
      },
      {
        id: 'created_by_name',
        title: 'Created by',
        renderComponent: (item) => (
          <Typography color={theme.foreground.black} fontSize={14}>
            {item?.created_by_name}
          </Typography>
        ),
      },
    ],
    [],
  );

  const renderActiveTab = () => (
    <Stack
      sx={{
        display: 'flex',
        flex: 1,
        overflowY: 'auto',
        mt: 2,
        position: 'relative',
      }}
      className='customized-scrollbar'
    >
      <DataLoading loading={isFetching} />
      {!isFetching && listAnnouncement.length === 0 ? (
        <Typography
          variant='fs12'
          textAlign='center'
          color='rgba(51,54,61, 0.4)'
          mt={4}
        >
          No data to display
        </Typography>
      ) : null}
      <Stack flexDirection='column' gap={0.5} color={theme.foreground.black}>
        {!isFetching
          ? listAnnouncement?.map((announcement) => (
              <Card
                sx={{
                  'p': 2,
                  'display': 'flex',
                  'flexDirection': 'row',
                  'gap': 2,
                  'alignItems': 'center',
                  'cursor': 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(237,240,247, .5)',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // eslint-disable-next-line no-underscore-dangle
                  handleClickDetail(announcement._id);
                }}
              >
                <Box
                  flex={1}
                  flexDirection='row'
                  display='flex'
                  gap={1}
                  alignItems='center'
                >
                  <Box
                    sx={{
                      'width': '120px',
                      'height': '88px',
                      'backgroundColor': '#EDF0F7',
                      'borderRadius': '4px',
                      'display': 'flex',
                      'alignItems': 'center',
                      'justifyContent': 'center',
                      'flexShrink': 0,
                      'position': 'relative',
                      '&:hover .preview-button': {
                        opacity: 1,
                      },
                    }}
                  >
                    {announcement.thumbnail ? (
                      <img
                        src={announcement.thumbnail}
                        alt={announcement.type}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : null}
                    <Box
                      className='preview-button'
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewAnnouncement(announcement);
                        }}
                        sx={{
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Icon name='eye-line' size={16} color='white' />
                        <Typography variant='fs14' color='white'>
                          Preview
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Typography
                      variant='fs14'
                      color='#717D96'
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 0.5,
                        alignItems: 'center',
                      }}
                    >
                      <Icon
                        name={
                          [
                            ANNOUNCEMENT_TYPE.GLOBAL_BANNER,
                            ANNOUNCEMENT_TYPE.HOME_PAGE_BANNER,
                          ].includes(announcement.type)
                            ? 'banner-fill'
                            : 'popup-fill'
                        }
                        size={12}
                        useBackgroundImg
                      />
                      {
                        ANNOUNCEMENT_TYPE_OPTIONS.find(
                          (op) => op.value === announcement.type,
                        )?.label
                      }
                    </Typography>
                    <TooltipBlock title={announcement.name}>
                      <Typography
                        variant='fs16'
                        fontWeight={700}
                        color={theme.foreground.black}
                      >
                        {announcement.name}
                      </Typography>
                    </TooltipBlock>
                    <Box
                      className='ql-editor'
                      sx={{
                        'mt': 0.25,
                        '& p': {
                          margin: 0,
                        },
                        '& *': {
                          fontSize: 14,
                          lineHeight: '24px',
                        },
                        '& a': {
                          'color': `${theme.palette.primary.main}`,
                          '&:visited': {
                            color: `${theme.palette.primary.main}`,
                          },
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: announcement.message_description,
                      }}
                      onClick={(e) => {
                        // Allow links to work without triggering detail view
                        if ((e.target as HTMLElement).tagName === 'A') {
                          e.stopPropagation();
                        }
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: '250px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                  }}
                >
                  {(() => {
                    const statusColor =
                      ANNOUNCEMENT_STATUS_COLOR[announcement.status];
                    const statusText =
                      announcement.status === ANNOUNCEMENT_STATUS.SCHEDULED
                        ? 'Scheduled'
                        : 'Live';

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                          }}
                        />
                        <Typography
                          variant='fs16'
                          fontWeight={700}
                          sx={{ color: statusColor }}
                        >
                          {statusText}
                        </Typography>
                      </Box>
                    );
                  })()}
                  <Typography variant='fs16' color={theme.foreground.black}>
                    {`${moment(announcement.start_time).format('MMM D, YYYY')} -
                    ${moment(announcement.end_time).format('MMM D, YYYY')}`}
                  </Typography>
                </Box>
              </Card>
            ))
          : null}
      </Stack>
    </Stack>
  );

  const renderArchivedTab = () => (
    <>
      <Divider />
      <Table
        outlined={false}
        stickyHeader
        data={listAnnouncement}
        columns={archivedColumns}
        onPageChange={(newPage) => {
          applyFilter({ page: newPage });
        }}
        // eslint-disable-next-line no-underscore-dangle
        onRowClick={(item) => handleClickDetail(item._id)}
        page={page}
        pageCount={data?.total_pages}
        pageStart={1}
        loading={isFetching}
        sx={{
          'flex': 1,
          'overflow': 'hidden',
          '& th': {
            backgroundColor: '#FBFBFC',
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            py: 0.5,
            fontWeight: 600,
          },
          '& td': {
            py: 1.5,
            px: 2,
            borderBottom: '1px solid #fff',
            verticalAlign: 'top',
          },
        }}
      />
    </>
  );

  return (
    <Layout
      title='Announcement'
      action={
        <Button
          startIconName='add'
          type='submit'
          onClick={() => navigate('create')}
          sx={{
            fontWeight: 500,
            fontFamily: 'Inter',
          }}
        >
          Create announcement
        </Button>
      }
      sx={{
        'minHeight': 'unset',
        'height': '100%',
        'overflowY': 'hidden',

        '>.MuiContainer-root': {
          'height': '100%',
          '>.MuiStack-root': { height: '100%' },
        },
      }}
    >
      <Stack
        sx={{
          mx: '-15px !important',
        }}
      >
        <Tabs
          type='page'
          value={tab}
          onChange={(_, t) => handleChangeTab(t)}
          sx={{
            '.Mui-selected': {
              fontWeight: 700,
            },
          }}
        >
          {ANNOUNCEMENT_TAB_LIST.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
        <Divider
          sx={{
            mx: '16px !important',
          }}
        />
      </Stack>
      <Card
        sx={{
          'p': 0,
          ...(tab === ANNOUNCEMENT_TAB.ARCHIVED && {
            flex: 1,
          }),
          'display': 'flex',
          'flexDirection': 'column',
          '.MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error)': {
            background: 'transparent',
          },
          '.ri-search-line': {
            fontSize: '16px !important',
          },
          'fieldset': {
            borderColor: 'transparent !important',
          },
          '& .MuiFormControl-root': {
            'minWidth': 0,
            'maxWidth': '100%',
            '& .MuiInputBase-root': {
              '& .MuiSvgIcon-root': {
                color: '#33363D !important',
                width: '21px !important',
              },
            },
          },
          '& .MuiSelect-select': {
            div: {
              color: '#33363D !important',
            },
          },
          '& .MuiSelect-icon': {
            color: '#33363D !important',
          },
          '.select-clearabled-icon': {
            'top': '5px',
            'fontSize': '16px !important',
            'color': `${theme.foreground.black} !important`,
            '&:hover': {
              color: `${theme.foreground.black} !important`,
            },
          },
        }}
      >
        <Controller outlined={false}>
          {({ mode }) => (
            <>
              <ModeSetting />
              <SearchForm
                key={tab?.toString()}
                placeholder='Search'
                value={search?.toString() || ''}
                onSubmit={(term) => {
                  applyFilterAndResetPage({ search: term });
                }}
                sx={{
                  'flex': 1,
                  '.MuiInputBase-root.Mui-focused fieldset': {
                    borderColor: '#2D91FC !important',
                  },
                  '.MuiInputBase-root:hover:not(.Mui-focused)': {
                    backgroundColor: 'rgba(237, 240, 247, 0.50)',
                  },
                }}
              />
              {mode !== TableController.Mode.Search && renderFilter()}
            </>
          )}
        </Controller>
        {tab === ANNOUNCEMENT_TAB.ARCHIVED ? renderArchivedTab() : null}
      </Card>
      {tab === ANNOUNCEMENT_TAB.ACTIVE ? renderActiveTab() : null}
      {previewAnnouncement && (
        <Preview
          open={!!previewAnnouncement}
          onClose={() => setPreviewAnnouncement(null)}
          announcement={previewAnnouncement}
        />
      )}
    </Layout>
  );
};

export default List;
